/**
 * UIStrings.ts
 * 
 * Defines UI strings used throughout the application. Centralizes all text content
 * for easy localization and maintenance. 
 */
/*! Copyright Jon Verrier 2025 */

export { EMultilineEditUIStrings } from './MultilineEditUIStrings';

const INCOSE_GTWR_SUMMARY_SHEET_URL = "https://www.incose.org/wp-content/uploads/legacy/working-groups/requirements-wg/guidetowritingrequirements/incose_rwg_gtwr_v4_summary_sheet.pdf";

// Type for the current linter mode
export enum ELinterMode {
   kRequirement = "requirement",
   kUserStory = "userstory"
}

// Common strings used across all linters
export interface ICommonUIStrings {
   kProcessing: string;
   kWarning: string;
   kInfo: string;
   kError: string;
   kSuccess: string;
   kServerErrorDescription: string;
   kHome: string;
   kPrivacyTitle: string;
   kTermsTitle: string;
   kPrivacy: string;
   kTerms: string;
}

// Strings that vary between a Requirement linter and a User Story linter
export interface ISpecificationUIStrings {
   kSpecificationLinterCaption: string;
   kSpecificationLinterStrapline: string;
   kSpecificationLinterDescription: string;
   kLinks: string;
   kRepo: string;
   kSpecificationPreamble: string;
   kSpecificationPlaceholder: string;
   kReviewPlaceholder: string;
   kImprovedPlaceholder: string;
   kNotASpecification: string;
}

// Combined interface that includes both common and requirement-specific strings
export type IUIStrings = ICommonUIStrings & ISpecificationUIStrings;


export const CommonUIStrings: ICommonUIStrings = {
   kProcessing: "Processing your request. This may take several seconds.",
   kWarning: "Warning:",
   kInfo: "Information:",
   kError: "Error:",
   kSuccess: "Success:",
   kServerErrorDescription: "Sorry, the linter could not get a response from the server, Please try again later.",
   kHome: "Home",
   kPrivacyTitle: "Privacy Policy",
   kTermsTitle: "Terms of Service",
   kPrivacy: "Privacy",
   kTerms: "Terms"
}

export const RequirementSpecificationUIStrings: ISpecificationUIStrings = {
   kSpecificationLinterCaption: "Requirement Linter",
   kSpecificationLinterStrapline: "Because no-one wants a fluffy Requirement.",
   kSpecificationLinterDescription: "Requirement Linter is an LLM-powered tool for reviewing and improving software requirements according to established guidelines. The Requirement Linter helps ensure requirements are clear, measurable, and properly structured. Requirement Linter prompts an LLM to review your requirements against the guidelines from the International Council of Systems Engineering 'Guide to Writing Requirements' (GtWR). The GtWR is a mature product that is widely used in the industry.",
   kLinks: `[The International Council of Systems Engineering 'Guide to Writing Requirements'](${INCOSE_GTWR_SUMMARY_SHEET_URL})`,
   kRepo: "[RequirementsLinter on GitHub](https://github.com/jonverrier/RequirementsLinter)",
   kSpecificationPreamble: "Enter the requirement you want to review in the edit box. Treat your content as public and do not enter confidential information:",
   kSpecificationPlaceholder: "Enter the requirement you want to review...",
   kReviewPlaceholder: "When your requirement is reviewed, the review will appear here.",
   kImprovedPlaceholder: "When your requirement is reviewed, a suggested alternative will appear here.",
   kNotASpecification: "The model does not categorize that text as a valid draft for a requirement. Please try again with an improved draft."
}


export const UserStorySpecificationUIStrings: ISpecificationUIStrings = {
   kSpecificationLinterCaption: "User Story Linter",
   kSpecificationLinterStrapline: "Because no-one wants a fluffy User Story.",
   kSpecificationLinterDescription: "User Story Linter is an LLM-powered tool for reviewing and improving user stories according to established guidelines. The User Story Linter helps ensure user stories are clear, measurable, and properly structured following the INVEST principles.",
   kLinks: "[Writing Good User Stories](https://www.gov.uk/service-manual/agile-delivery/writing-user-stories)," +
      "[How to Write Good User Stories](https://miro.com/agile/how-to-write-good-user-story/)," + 
      "[Writing Better User Stories](https://medium.com/theproductperson/writing-better-user-stories-through-active-acceptance-criteria-5615a54e2750)," +
      `[INCOSE Guide to Writing Requirements](${INCOSE_GTWR_SUMMARY_SHEET_URL})`,
   kRepo: "[RequirementsLinter on GitHub](https://github.com/jonverrier/RequirementsLinter)",
   kSpecificationPreamble: "Enter the user story you want to review in the edit box. Treat your content as public and do not enter confidential information:",
   kSpecificationPlaceholder: "Enter the user story you want to review...",
   kReviewPlaceholder: "When your user story is reviewed, the review will appear here.",
   kImprovedPlaceholder: "When your user story is reviewed, a suggested alternative will appear here.",
   kNotASpecification: "The model does not categorize that text as a valid draft for a user story. Please try again with an improved draft."
}

// Default UI strings that combine common and requirement strings
export const RequirementUIStrings: IUIStrings = {
   ...CommonUIStrings,
   ...RequirementSpecificationUIStrings
}

// Default UI strings that combine common and user story strings
export const UserStoryUIStrings: IUIStrings = {
   ...CommonUIStrings,
   ...UserStorySpecificationUIStrings
}

// Function to get the appropriate UI strings based on the current mode
export function getUIStrings(mode: ELinterMode): IUIStrings {
   switch (mode) {
      case ELinterMode.kRequirement:
         return RequirementUIStrings;
      case ELinterMode.kUserStory:
         return UserStoryUIStrings;
      default:
         return RequirementUIStrings;
   }
}

